// Notice that a NEW BUILD has been deployed while this tab has been sitting open.
// Ported from Facility Navigator (apps/web/src/lib/appVersion.ts), 2026-10-14.
//
// WHY. A single-page app keeps running the JavaScript it loaded at page load. A deploy cannot
// reach a tab that is already open, so someone who opened Summit in the morning is still on the
// morning's code at 3 PM, and every fix shipped in between is invisible to them.
//
// HOW. Create React App names the bundle by its content hash (/static/js/main.<hash>.js), so the
// name IS the build: no version number anyone has to remember to bump. We read the bundle this
// tab is running from its <script> tag, then now and then re-fetch index.html and compare. A
// different name means a deploy has landed underneath us. In development the bundle is
// bundle.js (no hash), so the check stays silent there.
//
// index.html is not under /api, and a restart mid-deploy or being offline reads as "don't know",
// never as "new version".

import { useEffect, useState } from 'react';

/** Long on purpose: the job is catching a tab left open for hours, not announcing a deploy the
 *  instant it lands. */
const CHECK_EVERY_MS = 10 * 60 * 1000;
/** However often the tab is focused and blurred, never check more than once a minute. */
const MIN_GAP_MS = 60 * 1000;

const BUNDLE_RE = /\/static\/js\/main\.[A-Za-z0-9]+\.js/;

/** The bundle this tab is running, or null when there isn't one to compare (dev server). */
function runningBundle() {
  try {
    for (const el of Array.from(document.querySelectorAll('script[src]'))) {
      const path = new URL(el.src, window.location.href).pathname;
      const m = path.match(BUNDLE_RE);
      if (m && m[0] === path) return path;
    }
  } catch { /* nothing worth failing over */ }
  return null;
}

async function deployedBundle() {
  const res = await fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } });
  if (!res.ok) return null;
  const m = (await res.text()).match(BUNDLE_RE);
  return m ? m[0] : null;
}

/** True once a build newer than this tab's is live. Latches: the only cure is a reload, and the
 *  nudge must not blink in and out while someone reads it. */
export function useNewVersionAvailable() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const running = runningBundle();
    if (!running) return undefined; // dev, or a page we don't recognise: say nothing

    let cancelled = false;
    let lastCheck = 0;
    let timer = 0;

    const check = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCheck < MIN_GAP_MS) return;
      lastCheck = now;
      let live = null;
      // Offline, a restart mid-deploy, a proxy hiccup: all mean "don't know", never "new version".
      try { live = await deployedBundle(); } catch { return; }
      if (cancelled || !live || live === running) return;
      setStale(true);
      window.clearInterval(timer); // nothing further to learn
    };

    timer = window.setInterval(check, CHECK_EVERY_MS);
    // The common case is someone coming back to a tab they left hours ago, so check on return too.
    document.addEventListener('visibilitychange', check);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  return stale;
}
